import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FixedSizeList as List } from 'react-window'

import Modal from 'component/Modal/GenericModal'
import DownloadIconSVG from 'component/svgComponents/download'
import { FORMAT_HOUR } from 'constants/config'
import moment from 'moment'

const TIME_LIMIT = moment('00:05:00', FORMAT_HOUR)

const COLUMN_STYLES = {
  order: { width: '12%', minWidth: '90px' },
  waitingTime: { width: '10%', minWidth: '80px' },
  date: { width: '15%', minWidth: '115px' },
  separationErrorDate: { width: '15%', minWidth: '115px' },
  releaseDate: { width: '15%', minWidth: '115px' },
  store: { width: '18%', minWidth: '140px' },
  reason: { width: '15%', minWidth: '100px' },
  details: { width: '9%', minWidth: '60px' }
}

const ROW_HEIGHT = 52

const OrderItemShape = PropTypes.shape({
  order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  excluded: PropTypes.bool,
  waitingTime: PropTypes.string,
  date: PropTypes.string,
  dateHour: PropTypes.string,
  separationErrorDate: PropTypes.string,
  releaseDate: PropTypes.string,
  store: PropTypes.string,
  exclusionReason: PropTypes.string
})

const getWaitingTimeClass = (order, colorizeTime = true) => {
  if (colorizeTime === false) return ''
  if (order.excluded) return 'order-waiting-time-not-excluded'
  if (moment(order.waitingTime, FORMAT_HOUR) > TIME_LIMIT) return 'order-waiting-time-exceded'
  return 'order-waiting-time-not-exceded'
}

const OrderRow = ({ index, style, data }) => {
  const { items, showReasons, colorizeTime, t, showDetailModal } = data
  const order = items[index]
  const isEven = index % 2 === 0

  return (
    <div 
      style={{ ...style, backgroundColor: isEven ? '#f9f9f9' : '#ffffff' }} 
      className="virtual-row"
    >
      <div style={COLUMN_STYLES.order} className="virtual-cell">{order.order}</div>
      {!showReasons && (
        <div
          style={COLUMN_STYLES.waitingTime}
          className={`virtual-cell ${getWaitingTimeClass(order, colorizeTime)}`}
        >
          {order.excluded
            ? t('totem.reports.modal.order.excluded')
            : order.waitingTime}
        </div>
      )}
      <div style={COLUMN_STYLES.date} className="virtual-cell">{`${order.date} ${order.dateHour}`}</div>
      <div style={COLUMN_STYLES.separationErrorDate} className="virtual-cell">{order.separationErrorDate ? order.separationErrorDate : '-'}</div>
      <div style={COLUMN_STYLES.releaseDate} className="virtual-cell">{order.releaseDate ? order.releaseDate : '-'}</div>
      
      <div style={COLUMN_STYLES.store} className="virtual-cell">{order.store}</div>
      {showReasons && <div style={COLUMN_STYLES.reason} className="virtual-cell">{order.exclusionReason}</div>}
      <div style={COLUMN_STYLES.details} className="virtual-cell virtual-cell-center">
        <span
          className='detail-icon-item'
          onClick={() => showDetailModal(order)}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 576 512'
            className='svg-inline--fa fa-eye fa-w-18 fa-3x'
          >
            <path
              d='M572.52 241.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400a144 144 0 1 1 144-144 143.93 143.93 0 0 1-144 144zm0-240a95.31 95.31 0 0 0-25.31 3.79 47.85 47.85 0 0 1-66.9 66.9A95.78 95.78 0 1 0 288 160z'
              className=''
            />
          </svg>
        </span>
      </div>
    </div>
  )
}

OrderRow.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  data: PropTypes.shape({
    items: PropTypes.arrayOf(OrderItemShape).isRequired,
    showReasons: PropTypes.bool,
    colorizeTime: PropTypes.bool,
    t: PropTypes.func.isRequired,
    showDetailModal: PropTypes.func.isRequired
  }).isRequired
}

const OrdersModal = ({
  closeModal,
  title,
  header,
  t,
  colorizeTime,
  showDetailModal,
  data,
  showReasons,
  onDownload,
}) => {
  const [isScrolled, setIsScrolled] = useState(false)

  const handleScroll = useCallback(({ scrollOffset }) => {
    setIsScrolled(scrollOffset > 0)
  }, [])

  return (
  <Modal
    title={title}
    size='lg'
    isOpen
    onCloseModal={closeModal}
    className='totem-report-modal'
    disabledBgClickClosing
    buttonClass='new-login-error-modal-button'
    confirmButtonText={
      <>
        <DownloadIconSVG className="download-svg-small" />
        <span style={{ color: '#fff' }}>{t('commonReport.download')}</span>
      </>
    }
    onConfirm={() => onDownload()}
    isConfirmButtonDisabled={!onDownload || !data || data.length === 0} 
    cancelButtonText={t('close_button_label')}
    contentBeforeChildren={
      header.length > 0 && (
        <div
          className='widgets-container'
          style={{
            padding: 0,
            marginBottom: 15,
            width: 756,
          }}
        >
          {header.map((element) => element)}
        </div>
      )
    }
  >
    <div className='table-compact order-modal'>
      <div className={`virtual-header ${isScrolled ? 'shadow-table-header' : ''}`}>
        <div style={COLUMN_STYLES.order} className="virtual-header-cell">{t('totem.reports.modal.order.detail.order')}</div>
        {!showReasons && (
          <div style={COLUMN_STYLES.waitingTime} className="virtual-header-cell">{t('totem.reports.modal.table.header.waiting.time')}</div>
        )}
        <div style={COLUMN_STYLES.date} className="virtual-header-cell">{t('totem.reports.modal.table.header.order.date')}</div>
        <div style={COLUMN_STYLES.separationErrorDate} className="virtual-header-cell">{t('totem.reports.modal.table.header.separation.error.date')}</div>
        <div style={COLUMN_STYLES.releaseDate} className="virtual-header-cell">{t('totem.reports.modal.table.header.release.date')}</div>
        <div style={COLUMN_STYLES.store} className="virtual-header-cell">{t('totem.reports.store.hint')}</div>
        {showReasons && (
          <div style={COLUMN_STYLES.reason} className="virtual-header-cell">{t('totem.reports.modal.table.header.reason')}</div>
        )}
        <div style={COLUMN_STYLES.details} className="virtual-header-cell virtual-header-cell-center">{t('see.details')}</div>
      </div>
      
      {data.length > 0 && (
        <div className="virtual-list-container" style={{ height: Math.min(data.length * ROW_HEIGHT, header.length > 0 ? 260 : 364) }}>
          <List
            height={Math.min(data.length * ROW_HEIGHT, header.length > 0 ? 260 : 364)}
            itemCount={data.length}
            itemSize={ROW_HEIGHT}
            width={'100%'}
            className="virtual-list-scroll"
            overscanCount={5}
            onScroll={handleScroll}
            itemData={{
              items: data,
              showReasons,
              colorizeTime: colorizeTime !== false,
              t,
              showDetailModal
            }}
          >
            {OrderRow}
          </List>
        </div>
      )}
    </div>
  </Modal>
  )
}

OrdersModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  confirmButtonText: PropTypes.string.isRequired,
  t: PropTypes.func,
  header: PropTypes.array,
  colorizeTime: PropTypes.bool,
  showDetailModal: PropTypes.func,
  data: PropTypes.array,
  showReasons: PropTypes.bool,
  onDownload: PropTypes.func
}

export default OrdersModal
